import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { ReactComponent as CloseIcon } from '../../static/icons/close.svg'
import { ReactComponent as DropdownPickerIcon } from '../../static/icons/dropdown-picker.svg'

import { inputClasses } from './Select.constants'
import type { SelectFieldProps } from './Select.types'

const Select = (props: SelectFieldProps) => {
  const {
    value,
    onChange,
    options,
    size = 'md',
    className = '',
    onFocus,
    isLoading = false,
    error = false,
    disabled,
  } = props
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value),
    [options, value]
  )

  const handleOptionClick = (optValue: string) => {
    if (!disabled) {
      onChange(optValue)
      setIsOpen(false)
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (!disabled) {
      onChange(null)
      setIsOpen(false)
    }
  }

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen((prev) => {
        const next = !prev

        if (next && onFocus) {
          onFocus()
        }

        return next
      })
    }
  }

  const selectClasses = classNames(
    inputClasses.base,
    inputClasses.size[size],
    className,
    {
      [inputClasses.status.error]: error,
      [inputClasses.status.success]: !error,
      [inputClasses.status.disabled]: disabled,
      [inputClasses.status.enabled]: !disabled,
    }
  )

  return (
    <div className="relative" ref={dropdownRef}>
      <div className={selectClasses} onClick={handleToggle}>
        {selectedOption
          ? selectedOption.label
          : t('Tenants.Profile.Licenses.Form.Actions.Select')}
      </div>

      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
        <DropdownPickerIcon
          className={`h-4 w-4 fill-current text-gray-300 transition-transform duration-200 ease-in-out ${
            isOpen ? 'rotate-180' : ''
          }`}
        />{' '}
      </div>

      {value && (
        <button
          type="button"
          className="absolute inset-y-0 right-8 flex items-center justify-center w-5 h-5 my-auto rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 hover:text-gray-700"
          onClick={handleClear}
        >
          <CloseIcon className="h-3 w-3" />
        </button>
      )}

      {isOpen && (
        <div className="absolute z-10 w-full bg-white border border-gray-300 mt-1 rounded-lg shadow-lg max-h-60 overflow-auto">
          {isLoading ? (
            <div className="p-2 text-center text-gray-500">
              {t('General.Loading')}
            </div>
          ) : options.length > 0 ? (
            options.map((opt) => (
              <div
                key={opt.value}
                className="p-2 hover:bg-gray-100 cursor-pointer text-left"
                onClick={() => handleOptionClick(opt.value)}
              >
                {opt.label}
              </div>
            ))
          ) : (
            <div className="p-2 text-center text-gray-400">
              {t('Tenants.Profile.Licenses.Form.Fields.NoOptions')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Select
